import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, Share2, ArrowRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ConfettiPiece({ delay, left, color }) {
  return (
    <div
      className="confetti-piece"
      style={{
        left: `${left}%`,
        backgroundColor: color,
        animationDelay: `${delay}s`,
        animationDuration: `${2 + Math.random() * 2}s`,
        borderRadius: Math.random() > 0.5 ? "50%" : "0",
        width: `${6 + Math.random() * 8}px`,
        height: `${6 + Math.random() * 8}px`,
      }}
    />
  );
}

export default function SuccessPage() {
  useSEO({ title: "Order Complete", description: "Your dog is officially a legend. Download your full HD sports trading card.", path: "/success" });
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [showConfetti, setShowConfetti] = useState(false);
  const pollRef = useRef(0);

  const pollPaymentStatus = useCallback(async () => {
    if (!sessionId) {
      setLoading(false);
      setPaymentStatus("missing");
      return;
    }

    pollRef.current += 1;
    const attempt = pollRef.current;

    try {
      const resp = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = resp.data;

      if (data.payment_status === "paid") {
        setPaymentStatus("paid");
        setShowConfetti(true);

        // Fetch full order details
        if (data.order_id) {
          try {
            const orderResp = await axios.get(`${API}/orders/${data.order_id}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              withCredentials: true,
            });
            setOrder(orderResp.data.order);
            setCard(orderResp.data.card);
            // GA4: purchase_completed event with revenue
            if (window.gtag) {
              const amt = orderResp.data.order?.amount_total;
              window.gtag('event', 'purchase_completed', {
                transaction_id: data.order_id,
                value: amt ? amt / 100 : 17,
                currency: 'USD',
              });
            }
            // Facebook Pixel: Purchase with revenue
            if (window.fbq) {
              const amt = orderResp.data.order?.amount_total;
              window.fbq('track', 'Purchase', { value: amt ? amt / 100 : 17, currency: 'USD' });
            }
          } catch {
            // Order details fetch failed, but payment is confirmed
            if (window.gtag) window.gtag('event', 'purchase_completed', { value: 17, currency: 'USD' });
            if (window.fbq) window.fbq('track', 'Purchase', { value: 17, currency: 'USD' });
          }
        }
        setLoading(false);
        return;
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
        setLoading(false);
        return;
      }

      // If still pending, keep polling (max 10 attempts)
      if (attempt < 10) {
        setTimeout(() => pollPaymentStatus(), 2000);
      } else {
        setPaymentStatus("timeout");
        setLoading(false);
      }
    } catch {
      if (attempt < 10) {
        setTimeout(() => pollPaymentStatus(), 2000);
      } else {
        setPaymentStatus("error");
        setLoading(false);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    pollPaymentStatus();
  }, [pollPaymentStatus]);

  // Hide confetti after 5s
  useEffect(() => {
    if (!showConfetti) return;
    const t = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(t);
  }, [showConfetti]);

  const triggerDownload = (format) => {
    if (order?.download_token) {
      const link = document.createElement("a");
      link.href = `${API}/download/${order.download_token}?format=${format}`;
      const name = (card?.dog_name || "card").replace(/\s+/g, "-").toLowerCase();
      const suffix = format === "instagram" ? "-instagram" : format === "wallpaper" ? "-wallpaper" : "";
      link.download = `card-my-dog-${name}${suffix}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (card?.full_image_url) {
      window.open(card.full_image_url, "_blank");
    }
  };

  const handleShare = async () => {
    const text = card
      ? `I turned my dog ${card.dog_name} into a ${card.sport} legend!`
      : "Check out my dog's sports card!";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Card My Dog", text, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch {
      /* ignore */
    }
  };

  const confettiColors = ["#F5A623", "#1A1A2E", "#2ECC71", "#E74C3C", "#3498DB", "#F39C12"];

  // Loading / polling state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-cream)]" data-testid="success-loading">
        <div className="text-center max-w-md px-4">
          <div className="w-12 h-12 border-4 border-[var(--color-gold)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-[var(--color-text-dark)] font-medium mb-1">Processing your payment...</p>
          <p className="font-body text-sm text-[var(--color-text-light)]">This may take a few seconds. Please don't close this page.</p>
        </div>
      </div>
    );
  }

  // No session_id
  if (paymentStatus === "missing") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-cream)]" data-testid="success-missing">
        <div className="text-center max-w-md px-4">
          <h2 className="font-heading text-3xl uppercase text-[var(--color-text-dark)] mb-4">No payment session found</h2>
          <p className="font-body text-[var(--color-text-mid)] mb-6">It looks like you arrived here without completing checkout.</p>
          <Link to="/create" className="inline-block font-heading tracking-widest uppercase px-8 py-3 rounded-[8px]" style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}>
            Create a Card
          </Link>
        </div>
      </div>
    );
  }

  // Payment failed / expired / timeout
  if (paymentStatus !== "paid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-cream)]" data-testid="success-failed">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: "rgba(231,76,60,0.1)" }}>
            <span className="text-2xl" style={{ color: "var(--color-error)" }}>!</span>
          </div>
          <h2 className="font-heading text-3xl uppercase text-[var(--color-text-dark)] mb-3">
            {paymentStatus === "expired" ? "Session Expired" : "Payment Processing"}
          </h2>
          <p className="font-body text-[var(--color-text-mid)] mb-6">
            {paymentStatus === "expired"
              ? "Your checkout session has expired. No charge was made."
              : "We couldn't confirm your payment yet. If you were charged, your card will be available in your email shortly."}
          </p>
          <Link to="/create" className="inline-block font-heading tracking-widest uppercase px-8 py-3 rounded-[8px]" style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }} data-testid="retry-create-btn">
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  // Payment successful
  return (
    <div className="min-h-screen py-8 md:py-16 relative overflow-hidden" data-testid="success-page">
      {/* Confetti */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {Array.from({ length: 40 }).map((_, i) => (
            <ConfettiPiece
              key={i}
              delay={Math.random() * 2}
              left={Math.random() * 100}
              color={confettiColors[i % confettiColors.length]}
            />
          ))}
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        {/* Gold checkmark */}
        <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: "var(--color-gold)" }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--color-navy)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-[var(--color-text-dark)] mb-3" data-testid="success-title">
          {card?.dog_name || "Your dog"} is officially a legend
        </h1>
        <p className="font-body text-[var(--color-text-mid)] mb-8">
          Check your email for your download link too
        </p>

        {/* Card preview */}
        {card && (card.full_image_url || card.preview_image_url) && (
          <div className="trading-card-frame mx-auto max-w-[320px] mb-8" data-testid="success-card">
            <img
              src={card.full_image_url || card.preview_image_url}
              alt={`${card.dog_name} trading card`}
              className="w-full aspect-[3/4] object-cover"
            />
          </div>
        )}

        {/* Download buttons — 3 formats */}
        <div className="flex flex-col gap-3 items-center mb-4 w-full max-w-md mx-auto">
          <button
            onClick={() => triggerDownload("original")}
            className="w-full font-heading tracking-widest uppercase text-base py-3.5 rounded-[8px] transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
            data-testid="download-original-btn"
          >
            <Download size={16} /> HD Card (Print-Ready)
          </button>
          <button
            onClick={() => triggerDownload("wallpaper")}
            className="w-full font-heading tracking-widest uppercase text-base py-3.5 rounded-[8px] transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: "var(--color-navy)", color: "white" }}
            data-testid="download-wallpaper-btn"
          >
            <Download size={16} /> Phone Wallpaper (9:16)
          </button>
          <button
            onClick={() => triggerDownload("instagram")}
            className="w-full font-heading tracking-widest uppercase text-base py-3.5 rounded-[8px] transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: "var(--color-navy)", color: "white" }}
            data-testid="download-instagram-btn"
          >
            <Download size={16} /> Instagram Post (1:1)
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-2 mb-8">
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-2 font-body text-sm px-6 py-3 rounded-[8px] border border-[var(--color-border)] text-[var(--color-text-mid)] hover:border-[var(--color-gold)] transition-colors bg-[var(--color-white)]"
            data-testid="share-success-btn"
          >
            <Share2 size={14} /> Share your legend
          </button>
          <Link
            to="/create"
            className="flex items-center justify-center gap-2 font-body text-sm px-6 py-3 rounded-[8px] border border-[var(--color-border)] text-[var(--color-text-mid)] hover:border-[var(--color-gold)] transition-colors bg-[var(--color-white)]"
            data-testid="create-another-btn"
          >
            <ArrowRight size={14} /> Create another card
          </Link>
        </div>

        {/* Order summary */}
        {order && (
          <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-6 text-left max-w-md mx-auto" data-testid="order-summary">
            <p className="font-accent text-xs tracking-widest uppercase mb-4" style={{ color: "var(--color-gold)" }}>
              Order Summary
            </p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-body text-sm text-[var(--color-text-mid)]">Order #</span>
                <span className="font-accent text-xs text-[var(--color-text-dark)]">{order.id?.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-body text-sm text-[var(--color-text-mid)]">Amount</span>
                <span className="font-body text-sm text-[var(--color-text-dark)]">${(order.amount_total / 100).toFixed(2)}</span>
              </div>
              {order.includes_print && (
                <div className="flex justify-between">
                  <span className="font-body text-sm text-[var(--color-text-mid)]">Print included</span>
                  <span className="font-body text-sm text-[var(--color-success)]">Yes</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="font-body text-sm text-[var(--color-text-mid)]">Email</span>
                <span className="font-body text-sm text-[var(--color-text-dark)]">{order.email}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
